import { useFilingCabinetContext } from '@/Contexts/Authenticated/FilingCabinetContext';
import { useUserContext } from '@/Contexts/Authenticated/UserContext';
import { useEventContext } from '@/Contexts/App/EventContext';
import { useWorklistsDashboardStoreContext } from '@/Stores/WorklistsDashboard/WorklistsDashboardStoreContext';
import { isEntityLoading } from '@/Models/Entity';
import { type FilingCabinetItem } from '@/Models/FilingCabinet/FilingCabinet';
import { ColumnsSetupDialog } from '@/Pages/GenericWorklist/Components/ColumnsSetupModal';
import WorklistStore from '@/Pages/GenericWorklist/Stores/WorklistStore';
import WorklistSortStore from '@/Pages/GenericWorklist/Stores/WorklistSortStore';
import { WorklistSelectionHelper } from '@/Pages/GenericWorklist/Stores/WorklistSelectionHelper';
import { CustomWorklistHelper } from '@/Pages/GenericWorklist/Stores/CustomWorklistHelper';
import { CurrentWorklistDashboardHelper } from '@/Pages/GenericWorklist/Stores/CurrentWorklistDashboardHelper';
import WorklistDataGridStore from './Components/DataGrid/WorklistDataGridStore';
import WorklistDataGridHandler from './Components/DataGrid/WorklistDataGridHandler';
import WorklistSidebarStore from './Components/Sidebar/WorklistSidebarStore';
import { WorklistToDashboardDialogViewStore } from './Components/Dialogs/WorklistToDashboardDialog/WorklistToDashboardDialogViewStore';
import { observer } from 'mobx-react-lite';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import WorklistFooter from './Components/DataGrid/Footer/WorklistFooter';
import WorklistDataGrid from './Components/DataGrid/WorklistDataGrid';
import WorklistSidebar from './Components/Sidebar/WorklistSidebar';
import Styles from './GenericWorklist.module.scss';

const {
    sideBar: sideBarStyle,
} = Styles;

interface GenericWorklistProps {
    initialState: FilingCabinetItem;
}

function WorklistPage({ initialState }: Readonly<GenericWorklistProps>): ReactElement {
    const fcContext = useFilingCabinetContext();
    const { user } = useUserContext();
    const eventContext = useEventContext();
    const worklistsDashboardStore = useWorklistsDashboardStoreContext();

    const store = useMemo(() => new WorklistStore(initialState, fcContext), [initialState]);

    const refreshResults = useCallback(() => {
        store.refetchResults({ preserveSelection: true })
            .catch((e) => console.error(`⛔ Refetching the results failed`, e));
    }, [store]);

    useEffect(() => {
        store.initializeStore()
            .catch((e) => {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }
                console.error(`⛔ Initializing the store failed`, e);
            });

        if (store.isReturningWorklist) {
            refreshResults();
        }

        return () => store.disposeStore();
    }, [store, refreshResults]);

    // Create ALL stores here, so we can follow a sort-of DI-approach (as proposed by AI)
    // At least this tries to keep a bit of the overview of which stores we're creating
    const worklistSortStore = useMemo(() => new WorklistSortStore(store), [store]);
    const worklistSelectionHelper = useMemo(() => new WorklistSelectionHelper(store), [store]);
    const worklistDataGridHandler = useMemo(() => new WorklistDataGridHandler(worklistSortStore, store), [store, worklistSortStore]);
    const dataGridStore = useMemo(() => new WorklistDataGridStore(), []);

    const customWorklistHelper = useMemo(
        () => new CustomWorklistHelper(eventContext, user, worklistsDashboardStore, store),
        [eventContext, user, worklistsDashboardStore, store],
    );

    const currentWorklistDashboardHelper = useMemo(
        () => new CurrentWorklistDashboardHelper(customWorklistHelper, worklistsDashboardStore),
        [customWorklistHelper, worklistsDashboardStore],
    );

    const worklistSidebarStore = useMemo(
        () => new WorklistSidebarStore(customWorklistHelper, store),
        [store, customWorklistHelper],
    );

    const worklistToDashboardDialogViewStore = useMemo(
        () => new WorklistToDashboardDialogViewStore(currentWorklistDashboardHelper, customWorklistHelper),
        [currentWorklistDashboardHelper, customWorklistHelper],
    );

    const resultsAreLoading = isEntityLoading(store.results);

    const setCurrTab = useCallback((tab: string) => store.setColumnsSetupDialogTab(tab), [store]);
    const closeCb = useCallback(() => store.setColumnsSetupModalOpen(false), [store]);

    return (
        <>
            <ColumnsSetupDialog
                open={store.columnsSetupModalOpen}
                setCurrTab={setCurrTab}
                tabIdx={store.columnsSetupDialogTab}
                closeCb={closeCb}
                setColumnData={store.setColumnViewSettings}
                setFilterData={store.setFilterViewSettings}
                viewData={store.worklist.View}
            />
            <div className={Styles.genericWorklist}>
                <div className={sideBarStyle}>
                    <WorklistSidebar
                        worklistStore={store}
                        customWorklistHelper={customWorklistHelper}
                        currentWorklistDashboardHelper={currentWorklistDashboardHelper}
                        worklistSidebarStore={worklistSidebarStore}
                        worklistToDashboardDialogViewStore={worklistToDashboardDialogViewStore}
                    />
                </div>
                <div className={Styles.dataContainer}>
                    <WorklistDataGrid
                        worklistStore={store}
                        worklistSortStore={worklistSortStore}
                        worklistSelectionHelper={worklistSelectionHelper}
                        worklistDataGridHandler={worklistDataGridHandler}
                        dataGridStore={dataGridStore}
                    />
                    <WorklistFooter
                        fullCount={store.fullCount}
                        fullCountOverflow={store.fullCountOverflow}
                        isLoading={resultsAreLoading}
                        paging={store.worklist.Params.Pagination}
                        refreshResults={refreshResults}
                        setPaging={store.applyPaging}
                        allLabels={store.availableLabels}
                        worklistStore={store}
                        selectionHelper={worklistSelectionHelper}
                    />
                </div>
            </div>
        </>
    );
}

export default observer(WorklistPage);
